import { memo, useEffect, useRef, useState } from "react";
import { Offcanvas } from "bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import MediaTabs from "./MediaTabs";
import { getToken } from "../../../function/token.jsx";
import domain from "../../../../reusecomponent/domain.jsx";
import { ErrorToastContainer } from "../../../function/CustomFunction.jsx";

/* ─── Skeleton shimmer styles (injected once, scoped to this component) ──── */
const skeletonCSS = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  .note-skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s ease-in-out infinite;
    border-radius: 10px;
  }
`;

const ContactInfoOffcanvas = memo(({ Contactinfo, SetContactinfo, wadetails }) => {
  const offcanvasRef = useRef(null);
  const bsInstance = useRef(null);
  const noteRef = useRef("");
  const [saving, setSaving]         = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);
  // Used to force the textarea to re-render with the fetched value
  const [noteKey, setNoteKey] = useState(0);

  // Initialise Bootstrap Offcanvas once on mount
  useEffect(() => {
    if (offcanvasRef.current) {
      bsInstance.current = new Offcanvas(offcanvasRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show offcanvas + reload note whenever Contactinfo becomes true
  useEffect(() => {
    if (Contactinfo) {
      bsInstance.current?.show();
      getNote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Contactinfo]);

  const getNote = async () => {
    setLoadingNote(true);
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/contact/note/${btoa(wadetails.wa_id)}`, {
        method: "GET",
        headers: new Headers({ Authorization: `Bearer ${token}` }),
        redirect: "follow",
      });
      const result = await response.json();
      if (result.status) {
        noteRef.current = result.data.note ?? "";
        // Bump key so textarea picks up the fresh defaultValue
        setNoteKey(k => k + 1);
      } else {
        ErrorToastContainer(result.message);
      }
    } catch (_) {
      ErrorToastContainer("Internal Server Error");
    } finally {
      setLoadingNote(false);
    }
  };

  const saveNote = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = getToken();
      const formdata = new FormData();
      formdata.append("note", noteRef.current);
      const response = await fetch(`${domain()}/contact/note/${btoa(wadetails.wa_id)}`, {
        method: "POST",
        headers: new Headers({ Authorization: `Bearer ${token}` }),
        body: formdata,
        redirect: "follow",
      });
      const result = await response.json();
      if (!result.status) ErrorToastContainer(result.message);
    } catch (_) {
      ErrorToastContainer("Internal Server Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      ref={offcanvasRef}
      id="contactInfoOffcanvas"
      style={{ width: "380px" }}
      aria-labelledby="contactInfoOffcanvasLabel"
    >
      {/* Inject shimmer keyframes once */}
      <style>{skeletonCSS}</style>

      {/* Header */}
      <div className="offcanvas-header py-3 border-bottom">
        <h5 id="contactInfoOffcanvasLabel" className="offcanvas-title fw-semibold">
          Contact info
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={() => SetContactinfo(false)}
          data-bs-dismiss="offcanvas"
          aria-label="Close contact info"
        />
      </div>

      {/* Body */}
      <div className="offcanvas-body">

        {/* ── Notes section ── */}
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <label
              className="form-label fw-semibold text-muted mb-0"
              style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.6px" }}
            >
              Notes
            </label>

            {/* Professional loading indicator — shown while API call is in-flight */}
            {loadingNote && (
              <div
                className="d-flex align-items-center gap-1"
                style={{ fontSize: "11px", color: "#667781" }}
                role="status"
                aria-live="polite"
              >
                {/* Animated dots */}
                <span
                  style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    backgroundColor: "rgb(39,92,83)",
                    display: "inline-block",
                    animation: "dotBounce 1s ease-in-out 0s infinite",
                  }}
                />
                <span
                  style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    backgroundColor: "rgb(39,92,83)",
                    display: "inline-block",
                    animation: "dotBounce 1s ease-in-out 0.2s infinite",
                  }}
                />
                <span
                  style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    backgroundColor: "rgb(39,92,83)",
                    display: "inline-block",
                    animation: "dotBounce 1s ease-in-out 0.4s infinite",
                  }}
                />
                <span className="ms-1 visually-hidden">Loading note…</span>
              </div>
            )}
          </div>

          {/* ── Skeleton placeholder while loading ── */}
          {loadingNote ? (
            <div aria-hidden="true">
              {/* Mimics the textarea shape */}
              <div
                className="note-skeleton"
                style={{ width: "100%", height: "100px", marginBottom: "8px" }}
              />
              {/* Mimics the save button area */}
              <div className="d-flex justify-content-end">
                <div
                  className="note-skeleton"
                  style={{ width: "90px", height: "22px", borderRadius: "6px" }}
                />
              </div>
            </div>
          ) : (
            /* ── Real textarea + save icon — shown after load ── */
            <div className="position-relative">
              <textarea
                key={noteKey}             /* re-mounts on fresh fetch so defaultValue applies */
                onChange={(e) => (noteRef.current = e.target.value)}
                defaultValue={noteRef.current}
                className="form-control"
                rows="4"
                placeholder="Add a note about this contact…"
                style={{
                  paddingRight: "40px",
                  borderRadius: "10px",
                  fontSize: "14px",
                  transition: "border-color 0.2s ease",
                }}
              />
              <div
                onClick={saveNote}
                title="Save note"
                style={{
                  position: "absolute",
                  bottom: "12px",
                  right: "12px",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ color: "rgb(39,92,83)" }}
                    role="status"
                  >
                    <span className="visually-hidden">Saving…</span>
                  </span>
                ) : (
                  <FontAwesomeIcon
                    icon={faSave}
                    style={{ color: "rgb(39,92,83)", fontSize: "16px" }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dot bounce keyframes (shared with save spinner color) */}
        <style>{`
          @keyframes dotBounce {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40%            { transform: scale(1);   opacity: 1;   }
          }
        `}</style>

        <MediaTabs Contactinfo={Contactinfo} wadetails={wadetails} />
      </div>
    </div>
  );
});

export default ContactInfoOffcanvas;
